/*
 * The browser reveals a focused control by its own box, without its `scroll-margin` and without the
 * card around it, so the focus outline a selection card draws for a control inside it (a card link, a
 * cover link, a checkbox, a visually hidden radio) can end under the fixed navigation. The card marked
 * `data-focus-reveal` is revealed instead: its `scroll-margin` covers the outline, the root's
 * `scroll-padding` the bars. Only a focus move that follows a key press: a text field matches
 * `:focus-visible` on a click too, and a pointer click or tap never scrolls the page.
 *
 * Not `scrollIntoView`: Chromium skips it when the card's border box fits but its margin box does not,
 * and lands up to a pixel short on fractional layouts. The page scroll is computed here, rounded
 * outwards, so the outline keeps the whole gap.
 *
 * A card that is still animating its own size (the `New list` morph) is revealed once more after its
 * running animations settle: the control inside it may have taken focus at a partial height, and the
 * script that ends the animation can re-focus an already focused control, which fires no `focusin`.
 */
export function initFocusReveal(documentRef = document) {
    let keyboardModality = false;
    const deferredCards = new WeakSet();

    documentRef.addEventListener('keydown', () => {
        keyboardModality = true;
    }, true);
    documentRef.addEventListener('pointerdown', () => {
        keyboardModality = false;
    }, true);
    documentRef.addEventListener('focusin', (event) => {
        const target = event.target;

        if (!keyboardModality || typeof target?.matches !== 'function' || !target.matches(':focus-visible')) {
            return;
        }

        const card = target.closest('[data-focus-reveal]');
        if (card) {
            revealCard(card, documentRef);
            revealAfterAnimations(card);
        }
    });

    // One deferred reveal per animation run, never re-armed by its own scroll.
    function revealAfterAnimations(card) {
        const animations = typeof card.getAnimations === 'function'
            ? card.getAnimations().filter((animation) => animation.playState === 'running' || animation.pending)
            : [];
        if (animations.length === 0 || deferredCards.has(card)) {
            return;
        }

        deferredCards.add(card);
        Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
            documentRef.defaultView.requestAnimationFrame(() => {
                deferredCards.delete(card);
                if (keyboardModality && card.contains(documentRef.activeElement)) {
                    revealCard(card, documentRef);
                }
            });
        });
    }
}

// `oversized` says which edge of a card taller than the area is shown: `start` (its top, the default) or
// `end` (its bottom, for a form whose last line and actions are what the user needs next).
export function revealScrollDelta(card, root, viewportHeight, getStyle, { oversized = 'start' } = {}) {
    const rootStyle = getStyle(root);
    const cardStyle = getStyle(card);
    const rect = card.getBoundingClientRect();
    const top = rect.top - (parseFloat(cardStyle.scrollMarginTop) || 0);
    const bottom = rect.bottom + (parseFloat(cardStyle.scrollMarginBottom) || 0);
    const areaTop = parseFloat(rootStyle.scrollPaddingTop) || 0;
    const areaBottom = viewportHeight - (parseFloat(rootStyle.scrollPaddingBottom) || 0);

    if (bottom - top > areaBottom - areaTop) {
        if (oversized === 'end') {
            return Math.ceil(bottom - areaBottom);
        }

        // Taller than the area: leave a card that already fills it, else show its top.
        return top < areaTop && bottom > areaBottom ? 0 : Math.floor(top - areaTop);
    }

    if (top < areaTop) {
        return Math.floor(top - areaTop);
    }

    return bottom > areaBottom ? Math.ceil(bottom - areaBottom) : 0;
}

function revealCard(card, documentRef) {
    const windowRef = documentRef.defaultView;
    const root = documentRef.documentElement;
    const delta = revealScrollDelta(card, root, root.clientHeight, (element) => windowRef.getComputedStyle(element));

    if (delta !== 0) {
        windowRef.scrollBy(0, delta);
    }
}
